import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Activity, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const ROLE_LABEL = { doctor: "Doctor", radiology_centre: "Diagnostic Centre", pharmacy: "Pharmacy" };

export default function InviteActivate() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/invite/${token}`).then((r) => { setInvite(r.data); setFullName(r.data.organization_name || ""); })
      .catch((e) => setError(e.response?.data?.detail || "Invitation not found or expired"))
      .finally(() => setLoading(false));
  }, [token]);

  const activate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/invite/${token}/activate`, { password, full_name: fullName, phone });
      localStorage.setItem("referu_token", data.token);
      setUser(data.user);
      const dest = data.user.role === "pharmacy" ? "/pharmacy" : data.user.role === "radiology_centre" ? "/centre" : "/dashboard";
      navigate(dest);
    } catch (e) { setError(e.response?.data?.detail || "Activation failed"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center"><Activity className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
          <span className="text-2xl font-bold tracking-tight text-slate-900 font-head">ReferU</span>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {error && !invite ? (
            <div className="text-center"><p className="text-red-600 font-medium" data-testid="invite-error">{error}</p>
              <Button onClick={() => navigate("/login")} className="mt-4 rounded-xl bg-blue-600 hover:bg-blue-700">Go to login</Button></div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold text-slate-900 font-head">Activate your account</h1>
              <p className="text-slate-500 mt-1 text-sm">You've been invited to ReferU as a <b>{ROLE_LABEL[invite?.role] || invite?.role}</b>.</p>
              <form onSubmit={activate} className="mt-6 space-y-4">
                <div><Label>Email</Label><Input value={invite?.email || ""} readOnly className="mt-1.5 h-12 rounded-xl bg-slate-50" /></div>
                <div><Label>Name / Organization</Label><Input data-testid="invite-name" value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-1.5 h-12 rounded-xl" /></div>
                <div><Label>Phone</Label><Input data-testid="invite-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-12 rounded-xl" /></div>
                <div><Label>Create Password</Label><Input data-testid="invite-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12 rounded-xl" /></div>
                {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="invite-form-error">{error}</p>}
                <Button data-testid="invite-activate-btn" type="submit" disabled={submitting} className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700">{submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Activate account"}</Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
